const ALLOWED_PLAN_KEYS = Object.freeze(['default', 'launch_promo', 'test'])

export const CHECKOUT_RESUME_I18N_KEYS = Object.freeze([
  'checkout.resume.continue',
  'checkout.resume.retryMount',
  'checkout.resume.inMemoryAvailable',
  'checkout.resume.mountFailedTitle',
  'checkout.resume.mountFailedBody',
  'checkout.resume.recoveryWait',
  'checkout.resume.planMismatch',
  'checkout.resume.unavailableUntil',
  'checkout.resume.localOnlyRecovery',
  'checkout.resume.reloadRecovery',
  'checkout.resume.reloadPage',
  'checkout.resume.processing',
  'checkout.resume.modalTitle',
  'checkout.resume.minutesRemaining',
])

const validPlanKey = planKey => typeof planKey === 'string'
  && ALLOWED_PLAN_KEYS.includes(planKey)

const timestamp = value => {
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

const nowFrom = clock => {
  const value = typeof clock === 'function' ? clock() : clock
  return Number.isFinite(value) ? value : NaN
}

export function normalizePendingCheckoutSession(
  payload,
  { source = 'initialized', receivedAt = Date.now() } = {},
) {
  if (!payload || typeof payload !== 'object') return null
  if (!['initialized', 'resumed'].includes(source)) return null
  if (typeof payload.checkoutFormContent !== 'string'
    || payload.checkoutFormContent.trim() === '') return null
  if (!validPlanKey(payload.planKey) || !Number.isFinite(receivedAt)) return null

  const hasExpiry = payload.checkoutExpiresAt !== undefined
    && payload.checkoutExpiresAt !== null
    && payload.checkoutExpiresAt !== ''
  if (!hasExpiry) {
    if (source !== 'initialized') return null
    return Object.freeze({
      checkoutFormContent: payload.checkoutFormContent,
      planKey: payload.planKey,
      checkoutExpiresAt: null,
      serverTime: null,
      receivedAt,
      source,
      expiryMode: 'unknown',
      serverResumable: false,
    })
  }

  const checkoutExpiresAt = timestamp(payload.checkoutExpiresAt)
  const serverTime = timestamp(payload.serverTime)
  if (checkoutExpiresAt === null || serverTime === null) return null

  return Object.freeze({
    checkoutFormContent: payload.checkoutFormContent,
    planKey: payload.planKey,
    checkoutExpiresAt: new Date(checkoutExpiresAt).toISOString(),
    serverTime: new Date(serverTime).toISOString(),
    receivedAt,
    source,
    expiryMode: 'known',
    serverResumable: source === 'resumed' || payload.resumable === true,
  })
}

export function checkoutServerNow(session, clock = Date.now) {
  if (!session || session.expiryMode !== 'known') return null
  const serverTime = timestamp(session.serverTime)
  const localNow = nowFrom(clock)
  if (serverTime === null || !Number.isFinite(session.receivedAt) || !Number.isFinite(localNow)) {
    return null
  }
  return serverTime + Math.max(0, localNow - session.receivedAt)
}

export function evaluatePendingCheckoutSession(session, planKey, clock = Date.now) {
  if (!session || typeof session !== 'object' || !validPlanKey(planKey)) return 'invalid'
  if (typeof session.checkoutFormContent !== 'string'
    || session.checkoutFormContent.trim() === ''
    || !validPlanKey(session.planKey)
    || !['known', 'unknown'].includes(session.expiryMode)) return 'invalid'

  if (session.expiryMode === 'known') {
    const expiresAt = timestamp(session.checkoutExpiresAt)
    const serverNow = checkoutServerNow(session, clock)
    if (expiresAt === null || serverNow === null) return 'invalid'
    if (expiresAt <= serverNow) return 'expired'
  }

  if (session.planKey !== planKey) return 'plan-mismatch'
  return 'resumable'
}

export function pendingCheckoutRemainingMs(session, clock = Date.now) {
  if (!session || session.expiryMode !== 'known') return null
  const expiresAt = timestamp(session.checkoutExpiresAt)
  const serverNow = checkoutServerNow(session, clock)
  if (expiresAt === null || serverNow === null) return null
  return Math.max(0, expiresAt - serverNow)
}
